import { motion } from "framer-motion";
import { ArrowRight, Check, ClipboardList, GraduationCap, HeartHandshake, MapPin, Rocket, Sparkles, TrendingUp, Users } from "lucide-react";
import { MkCTA } from "@/components/marketing/MarketingLayout";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, delay },
});

const JOURNEY = [
  { icon: Rocket, title: "Join free in 2 minutes", copy: "Tell us where you're headed. That's all we need to start." },
  { icon: ClipboardList, title: "Get your personal roadmap", copy: "Your journey tracker shows exactly what to do, in what order." },
  { icon: GraduationCap, title: "Attend your pre-flight induction", copy: "One free session that replaces weeks of anxious Googling." },
  { icon: Users, title: "Meet your city circle", copy: "Get introduced to students moving to your city before you fly." },
  { icon: MapPin, title: "Land prepared", copy: "SIM, bank, transport, groceries, day one feels like week four." },
  { icon: TrendingUp, title: "Keep growing", copy: "Jobs, deals, events and adventures through your entire degree." },
];

const BENEFITS = [
  "Free membership, forever",
  "Pre-flight induction with real students",
  "City circles before you land",
  "Castles AI, your 24/7 advisor",
  "Member-only deals and discounts",
  "Part-time job and CV guidance",
  "Members-only events and trips",
  "City guides written by locals",
];

const TRACKER_PREVIEW = [
  "Join Castles", "Attend Online Induction", "Book Accommodation", "Order UK SIM",
  "Open UK Bank Account", "Prepare UK CV", "Find Part-Time Job", "Attend First Castles Event",
];

export default function Membership() {
  return (
    <main data-testid="membership-page">
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 sm:pt-28 pb-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
          <p className="text-sm font-semibold tracking-[0.15em] uppercase text-[#D4AF37]">Membership</p>
          <h1 className="mt-5 text-4xl sm:text-5xl font-bold tracking-tight text-[#0A1633] leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="membership-heading">
            Everything you need for your UK life. One free membership.
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            No tiers. No trials. No card details. Join once and unlock the complete platform: guidance, people, deals and tools for your entire student journey.
          </p>
          <div className="mt-10 flex items-center gap-5 flex-wrap">
            <MkCTA testid="membership-hero-cta" />
            <p className="text-sm text-slate-500">Free forever · Cancel anytime (you won't want to)</p>
          </div>
        </motion.div>
      </section>

      <section className="bg-white border-y border-slate-100" data-testid="member-journey">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
          <motion.div {...fade()}>
            <p className="text-sm font-semibold tracking-[0.15em] uppercase text-[#D4AF37]">The member journey</p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-[#0A1633]" style={{ fontFamily: "'Outfit', sans-serif" }}>From sign-up to settled, step by step</h2>
          </motion.div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {JOURNEY.map((j, i) => (
              <motion.div key={j.title} {...fade((i % 3) * 0.07)} data-testid={`journey-step-${i}`}
                className="bg-[#FDFDFC] border border-slate-100 rounded-3xl p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)]">
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-2xl bg-[#10B981]/10 flex items-center justify-center">
                    <j.icon className="w-6 h-6 text-[#10B981]" strokeWidth={1.5} />
                  </div>
                  <span className="text-4xl font-bold text-slate-100" style={{ fontFamily: "'Outfit', sans-serif" }}>{i + 1}</span>
                </div>
                <h3 className="mt-6 text-lg font-semibold text-[#0A1633]" style={{ fontFamily: "'Outfit', sans-serif" }}>{j.title}</h3>
                <p className="mt-2.5 text-slate-600 leading-relaxed">{j.copy}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32" data-testid="member-experience">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div {...fade()}>
            <p className="text-sm font-semibold tracking-[0.15em] uppercase text-[#D4AF37]">Your progress tracker</p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-[#0A1633]" style={{ fontFamily: "'Outfit', sans-serif" }}>Always know exactly what to do next</h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              Your member portal includes a personal UK journey tracker. Every task you tick off is one less thing to worry about, and one step closer to feeling at home.
            </p>
            <ul className="mt-8 space-y-4">
              {["Never wonder 'am I forgetting something?' again", "Built from hundreds of real student journeys", "Celebrate every milestone, big or small"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-slate-700">
                  <span className="w-6 h-6 rounded-full bg-[#22C55E]/15 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-[#22C55E]" strokeWidth={2.5} />
                  </span>
                  <span className="leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div {...fade(0.1)} className="bg-white border border-slate-100 shadow-[0_20px_40px_rgb(0,0,0,0.08)] rounded-3xl p-8 sm:p-10">
            <p className="text-sm font-semibold text-[#0A1633]">Your UK Journey</p>
            <div className="mt-4 space-y-3">
              {TRACKER_PREVIEW.map((t, i) => (
                <div key={t} className="flex items-center gap-4 rounded-2xl border border-slate-100 px-5 py-3.5" data-testid={`tracker-preview-${i}`}>
                  <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${i < 2 ? "bg-[#22C55E] border-[#22C55E]" : "border-slate-200"}`}>
                    {i < 2 && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                  </span>
                  <span className={`text-sm ${i < 2 ? "text-slate-400 line-through" : "text-slate-700"}`}>{t}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-white border-y border-slate-100" data-testid="member-benefits">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
          <motion.div {...fade()}>
            <p className="text-sm font-semibold tracking-[0.15em] uppercase text-[#D4AF37]">Full benefits</p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-[#0A1633]" style={{ fontFamily: "'Outfit', sans-serif" }}>What every member receives</h2>
          </motion.div>
          <div className="mt-12 grid sm:grid-cols-2 gap-x-12 gap-y-5 max-w-4xl">
            {BENEFITS.map((b, i) => (
              <motion.div key={b} {...fade(i * 0.04)} className="flex items-center gap-4" data-testid={`benefit-${i}`}>
                <span className="w-7 h-7 rounded-full bg-[#D4AF37]/15 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                </span>
                <span className="text-slate-700 text-lg">{b}</span>
              </motion.div>
            ))}
          </div>
          <motion.div {...fade(0.2)} className="mt-14">
            <MkCTA testid="membership-benefits-cta">
              Unlock Everything for Free <ArrowRight className="inline w-4 h-4 ml-1" />
            </MkCTA>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
        <motion.div {...fade()} className="text-center max-w-2xl mx-auto">
          <HeartHandshake className="w-10 h-10 text-[#10B981] mx-auto" strokeWidth={1.5} />
          <h2 className="mt-6 text-3xl sm:text-4xl font-bold tracking-tight text-[#0A1633]" style={{ fontFamily: "'Outfit', sans-serif" }}>
            The best time to join was when you got your offer letter. The second best time is today.
          </h2>
          <div className="mt-10">
            <MkCTA testid="membership-final-cta" />
          </div>
        </motion.div>
      </section>
    </main>
  );
}
